import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import Table from "@material-ui/core/Table";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import TableHead from "@material-ui/core/TableHead";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";

import ThreeDotMenu, { ThreeDotMenuProps } from "components/ThreeDotMenu";

import { User, UserMultipleRoles } from "data/api";
import fullName from "utils/fullName";
import { useSort } from "utils/sortUtils";

export const useStyles = makeStyles((theme: Theme) => ({
  pending: {
    "& .MuiTableCell-body": {
      color: theme.palette.grey[500],
    },
  },
  threeDotMenu: {
    padding: "0",
  },
}));

type FullNameUser = User & {
  name: string;
};

type Props = {
  users: User[];
  isActive?: boolean;
  threeDotMenuProps?: (user: User) => ThreeDotMenuProps;
  editable?: boolean;
};

const UsersTable: React.FC<Props> = ({ users, isActive, threeDotMenuProps, editable = true }) => {
  const [t] = useTranslation();
  const classes = useStyles();
  const showThreeDotMenu = Boolean(threeDotMenuProps) && editable;
  const [sortOrder, sort] = useSort<FullNameUser>("name", ["name", "email", "phoneNumber", "role"]);
  const usersWithFullnames = useMemo(() => users.map((user) => ({ ...user, name: fullName(user) })), [users]);
  const sortedUsers = useMemo(() => sort(usersWithFullnames), [sort, usersWithFullnames]);

  const getOneOrManyRoles = useCallback((user: User) => {
    return user.projectRoles?.map((role) => role.role).join(", ");
  }, []);

  return (
    <Table aria-label={t("usersTable.ariaLabel")}>
      <TableHead>
        <TableRow>
          <TableCell>
            <TableSortLabel
              active={sortOrder.name?.active}
              direction={sortOrder.name?.order}
              onClick={sortOrder.name?.onSort}
            >
              {t("usersTable.name")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel
              active={sortOrder.email?.active}
              direction={sortOrder.email?.order}
              onClick={sortOrder.email?.onSort}
            >
              {t("usersTable.email")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel
              active={sortOrder.phoneNumber?.active}
              direction={sortOrder.phoneNumber?.order}
              onClick={sortOrder.phoneNumber?.onSort}
            >
              {t("usersTable.phone")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel
              active={sortOrder.role?.active}
              direction={sortOrder.role?.order}
              onClick={sortOrder.role?.onSort}
            >
              {t("usersTable.role")}
            </TableSortLabel>
          </TableCell>
          <TableCell></TableCell>
          {showThreeDotMenu && <TableCell></TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedUsers.map((user) => (
          <TableRow key={user.id} className={classnames({ [classes.pending]: !user.isRegistered })}>
            <TableCell>{user.name}</TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell>{user.phoneNumber}</TableCell>
            <TableCell>{getOneOrManyRoles(user)}</TableCell>
            <TableCell>{user.isRegistered ? "" : t("usersTable.pending")}</TableCell>
            {showThreeDotMenu && (
              <TableCell>
                <ThreeDotMenu
                  buttonProps={{ disabled: !isActive, className: classes.threeDotMenu }}
                  {...threeDotMenuProps!(user)}
                />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default UsersTable;
